import { message } from 'antd';
import { MutableRefObject } from 'react';

import { cancelUserCoupon } from '@/components/UIComponents/UserAccount/service';
import { UseTranslateType } from '@/hooks/useTranslate';
import { orderPay, orderPolling } from '@/services/order';
import UserStore from '@/store/User';
import { getCookie } from '@/utils';
import { AIFunction } from '@/utils/const';
import { isCnArea } from '@/utils/envHelper';
import { fc_purchase_alert_expose } from '@/utils/eventlog/eventNewConfig';

import { getFeatureSource } from '../utils';
import { OrderInfo } from './usePayment';
type Channel = {
  type: 'qrcode' | string;
  data: string;
};
export type Callback = {
  success: (orderId: string, data?: any) => void;
  error: (msg: string) => void;
};

interface ChannelPayProps {
  feature: fc_purchase_alert_expose['from'];
  commodityCode: string;
  couponCode: string;
  channel?: Channel;
  callback: Callback;
}
interface ChannelPollingProps {
  orderInfo: OrderInfo;
  t: UseTranslateType | undefined;
  pollPayKey: string;
  timer: MutableRefObject<NodeJS.Timeout | null>;
  callback: { success: () => void; error: (msg: string) => void };
}

function formatParams(
  feature: AIFunction,
  commodityCode: string,
  couponCode: string,
  channel?: Channel,
) {
  const areaKey = isCnArea ? 'cn' : 'oversea';

  const config = {
    cn: {
      channel: channel?.data || 'alipay_pc_direct',
      returnUrl: 'https://marketing.merrorai.cn/payTransform',
      country: 'CN',
    },
    oversea: {
      channel: 'stripe',
      returnUrl: 'https://studio.boomcut.ai/payTransform',
      country: 'US',
    },
  };

  // 公参
  const baseParams = {
    extend: JSON.stringify({
      uid: getCookie('user').uid,
      source: getFeatureSource(feature),
    }),
    commodityCode: String(commodityCode),
    ...config[areaKey],
  };

  return isCnArea ? { ...baseParams, couponCode } : {};
}
export const channelPay = async ({
  feature = AIFunction.Other,
  commodityCode,
  couponCode,
  channel,
  callback,
}: ChannelPayProps) => {
  try {
    const params = formatParams(feature, commodityCode, couponCode, channel);
    const { data, success, message: msg } = await orderPay(params);
    if (!success) {
      callback.error(msg || '/rest/dlt/commerce/prepayment callback error');
    } else {
      callback.success(data.orderId, { url: data.codeUrl });
      if (channel?.type === 'qrcode') {
        return data.codeUrl;
      } else {
        const paymentWindow = window.open('', '_blank');
        paymentWindow?.document.write(
          '<html><head><title>支付宝支付</title></head><body>' +
            data.codeUrl +
            '</body></html>',
        );
      }
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const channelPolling = ({
  orderInfo,
  t,
  pollPayKey,
  timer,
  callback,
}: ChannelPollingProps) => {
  return new Promise<void>((resolve, reject) => {
    const poll = async (attempts: number) => {
      timer.current && clearTimeout(timer.current);
      try {
        if (attempts > 60 * 2) {
          callback.error('timeout');

          timer.current && clearTimeout(timer.current);

          const couponInfo = UserStore.getState().couponInfo;

          if (couponInfo.type === 'ticket') {
            if (couponInfo.availabled?.length) {
              for (const item of couponInfo.availabled) {
                if (item.applicableGoods.includes(orderInfo.orderCommodityCode)) {
                  cancelUserCoupon(item.couponCode);
                }
              }
            }
          }

          return resolve();
        }

        const data = await orderPolling({ orderId: orderInfo.orderId });

        if (data.success && data.data === 'success') {
          callback.success();
          timer.current && clearTimeout(timer.current);
          return resolve();
        } else {
          timer.current = setTimeout(() => poll(attempts + 1), 2000);
          if (attempts === 60) {
            message.open({
              key: pollPayKey,
              type: 'warning',
              content: t && t('merror.sub.noresult.toast'),
              duration: 3.5,
            });
          }
        }
      } catch (error) {
        timer.current && clearTimeout(timer.current);
        callback.error((error as any).message || 'loop fail');
        return reject(error);
      }
    };
    if (!orderInfo.orderId) {
      return;
    }
    poll(0);
  });
};
